/**
 * Pitch — fundamental note representation.
 *
 * A Pitch is a (letter, accidental, octave) triple. Two pitches with different
 * spellings but the same MIDI number are enharmonically equivalent (e.g. C# and Db).
 * The engine preserves spelling everywhere — this matters for music theory
 * (a C# major scale spells differently than Db major even though they sound identical).
 */

export type Letter = "C" | "D" | "E" | "F" | "G" | "A" | "B";

/** Accidental as a signed offset from the natural letter. -2 = double flat, +2 = double sharp. */
export type Accidental = -2 | -1 | 0 | 1 | 2;

export interface Pitch {
  letter: Letter;
  accidental: Accidental;
  /** Scientific octave: middle C = C4. */
  octave: number;
}

export type PitchClass = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11;

const LETTER_TO_SEMITONE: Record<Letter, number> = {
  C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11,
};

const LETTERS: Letter[] = ["C", "D", "E", "F", "G", "A", "B"];

const ACCIDENTAL_NAMES: Record<Accidental, string> = {
  [-2]: "bb",
  [-1]: "b",
  [0]: "",
  [1]: "#",
  [2]: "##",
};

/** Construct a pitch from its parts. */
export function pitch(letter: Letter, accidental: Accidental, octave: number): Pitch {
  return { letter, accidental, octave };
}

/** MIDI number — middle C (C4) = 60. */
export function toMidi(p: Pitch): number {
  return (p.octave + 1) * 12 + LETTER_TO_SEMITONE[p.letter] + p.accidental;
}

/** Pitch class (0-11), ignoring octave. C = 0, C#/Db = 1, ..., B = 11. */
export function pitchClass(p: Pitch): PitchClass {
  const pc = ((LETTER_TO_SEMITONE[p.letter] + p.accidental) % 12 + 12) % 12;
  return pc as PitchClass;
}

/** Frequency in Hz, with A4 = 440. */
export function toFrequency(p: Pitch, a4 = 440): number {
  const midi = toMidi(p);
  return a4 * Math.pow(2, (midi - 69) / 12);
}

/** Render as a string like "C#4", "Bb3", "F##5". */
export function pitchToString(p: Pitch): string {
  return `${p.letter}${ACCIDENTAL_NAMES[p.accidental]}${p.octave}`;
}

/**
 * Parse a pitch string. Accepts: "C4", "C#4", "Db4", "C##4", "Cbb4".
 * Also accepts double-character ASCII forms: "C##" and "Cbb".
 * Returns null on malformed input.
 */
export function parsePitch(s: string): Pitch | null {
  const match = s.match(/^([A-G])(##|bb|#|b)?(-?\d+)$/);
  if (!match) return null;
  const letter = match[1] as Letter;
  const accStr = match[2] ?? "";
  const octave = parseInt(match[3]!, 10);
  const accMap: Record<string, Accidental> = {
    "": 0, "#": 1, "b": -1, "##": 2, "bb": -2,
  };
  const accidental = accMap[accStr];
  if (accidental === undefined) return null;
  return { letter, accidental, octave };
}

/** Construct a pitch from a MIDI number using a preferred sharp/flat spelling. */
export function pitchFromMidi(midi: number, preferSharps = true): Pitch {
  const octave = Math.floor(midi / 12) - 1;
  const pc = ((midi % 12) + 12) % 12;
  // Natural notes get their letter; accidentals depend on preference.
  const sharpSpelling: Array<[Letter, Accidental]> = [
    ["C", 0], ["C", 1], ["D", 0], ["D", 1], ["E", 0], ["F", 0],
    ["F", 1], ["G", 0], ["G", 1], ["A", 0], ["A", 1], ["B", 0],
  ];
  const flatSpelling: Array<[Letter, Accidental]> = [
    ["C", 0], ["D", -1], ["D", 0], ["E", -1], ["E", 0], ["F", 0],
    ["G", -1], ["G", 0], ["A", -1], ["A", 0], ["B", -1], ["B", 0],
  ];
  const [letter, accidental] = (preferSharps ? sharpSpelling : flatSpelling)[pc]!;
  return { letter, accidental, octave };
}

/** Two pitches sound the same (same MIDI), regardless of spelling. */
export function isEnharmonic(a: Pitch, b: Pitch): boolean {
  return toMidi(a) === toMidi(b);
}

/** Letter index 0..6 for C..B. Used by interval arithmetic. */
export function letterIndex(letter: Letter): number {
  return LETTERS.indexOf(letter);
}

/** Inverse of letterIndex. */
export function letterFromIndex(i: number): Letter {
  const wrapped = ((i % 7) + 7) % 7;
  return LETTERS[wrapped]!;
}

/** Step a letter by N diatonic steps (positive = up). */
export function stepLetter(letter: Letter, steps: number): Letter {
  return letterFromIndex(letterIndex(letter) + steps);
}

/**
 * Transpose a pitch by a given number of letter steps and semitones,
 * preserving spelling correctness. Used as the primitive for interval transposition.
 */
export function transposeByLetterAndSemitones(
  p: Pitch,
  letterSteps: number,
  semitones: number,
): Pitch {
  const targetLetter = stepLetter(p.letter, letterSteps);
  // Determine what octave the target letter falls into based on letter motion.
  const startLetterAbs = letterIndex(p.letter);
  const newLetterAbs = startLetterAbs + letterSteps;
  const octaveShift = Math.floor(newLetterAbs / 7);
  const baseOctave = p.octave + octaveShift;
  // Compute the natural MIDI of the target, then derive the accidental.
  const naturalMidi = (baseOctave + 1) * 12 + LETTER_TO_SEMITONE[targetLetter];
  const targetMidi = toMidi(p) + semitones;
  const accidental = targetMidi - naturalMidi;
  if (accidental < -2 || accidental > 2) {
    throw new Error(
      `Transposition produces unrepresentable accidental: ${accidental} on ${targetLetter}`,
    );
  }
  return { letter: targetLetter, accidental: accidental as Accidental, octave: baseOctave };
}
